import Breadcrumb from "components/Common/Breadcrumb";
import TableContainer from "components/Common/DataTableContainer";
import DashboardLoader from "components/Loader/DashboardLoader";
import DeleteModal from "pages/Calendar/DeleteModal";
import React, { useMemo, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  useGetAllJobApplicationsQuery,
  useDeleteJobApplicationMutation,
  useGetAllJobsForFilterQuery,
} from "store/features/jobs/jobApplicationApi";
import EditJobApplicationModal from "components/Modals/EditJobApplicationModal";

const AllJobApplications = () => {
  const { data: applications, isLoading, refetch } = useGetAllJobApplicationsQuery();
  const { data: jobs, isLoading: jobsLoading } = useGetAllJobsForFilterQuery();
  const [deleteJobApplication, { isLoading: deletingApplication }] = useDeleteJobApplicationMutation();

  const [deleteModal, setDeleteModal] = useState(false);
  const [selectedApplicationId, setSelectedApplicationId] = useState(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedApplication, setSelectedApplication] = useState(null);

  // Filter states
  const [jobTitleFilter, setJobTitleFilter] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  const showDeleteModal = id => {
    setSelectedApplicationId(id);
    setDeleteModal(true);
  };

  const handleDeleteApplication = async () => {
    try {
      await deleteJobApplication(selectedApplicationId).unwrap();
      refetch();
      setDeleteModal(false);
    } catch (error) {
      console.error("Failed to delete application:", error);
    }
  };

  const handleEditApplication = application => {
    setSelectedApplication(application);
    setEditModalOpen(true);
  };

  const handleApplicationUpdate = () => {
    refetch();
    setEditModalOpen(false);
  };

  // Get unique job titles from applications
  const uniqueJobTitles = useMemo(() => {
    if (!applications) return [];
    const titles = applications
      .map(app => app.title)
      .filter((title, index, self) => title && self.indexOf(title) === index);
    return titles.sort();
  }, [applications]);

  // Filter applications based on filters
  const filteredApplications = useMemo(() => {
    if (!applications) return [];

    return applications.filter(application => {
      // Job title filter
      if (jobTitleFilter && application.title !== jobTitleFilter) {
        return false;
      }

      // Status filter
      if (statusFilter) {
        const status = application.applystatus || application.selectionstatus;
        if (status !== statusFilter) {
          return false;
        }
      }

      // Date filter
      if (startDate || endDate) {
        const appDate = new Date(application.date.split('/').reverse().join('-'));

        if (startDate && endDate) {
          const start = new Date(startDate);
          const end = new Date(endDate);
          end.setHours(23, 59, 59, 999); // Include entire end day
          if (appDate < start || appDate > end) {
            return false;
          }
        } else if (startDate) {
          const start = new Date(startDate);
          if (appDate < start) {
            return false;
          }
        } else if (endDate) {
          const end = new Date(endDate);
          end.setHours(23, 59, 59, 999);
          if (appDate > end) {
            return false;
          }
        }
      }

      return true;
    });
  }, [applications, jobTitleFilter, startDate, endDate, statusFilter]);

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return dateString;
  };

  const getStatusBadge = (application) => {
    let status = "Applied";
    let color = "primary";

    if (application.interviewstatus === "completed") {
      status = "Interview Done";
      color = "info";
    } else if (application.interviewstatus === "scheduled") {
      status = "Interview Scheduled";
      color = "warning";
    }

    if (application.selectionstatus === "selected") {
      status = "Selected";
      color = "success";
    } else if (application.selectionstatus === "rejected") {
      status = "Rejected";
      color = "danger";
    }

    return `<span class="badge bg-${color}">${status}</span>`;
  };

  const handleCVDownload = (cvUrl) => {
    if (!cvUrl) return;

    const link = document.createElement('a');
    link.href = cvUrl;
    link.target = '_blank';
    link.download = cvUrl.split('/').pop() || 'CV.pdf';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleClearFilters = () => {
    setJobTitleFilter("");
    setStartDate("");
    setEndDate("");
    setStatusFilter("");
  };

  const columns = useMemo(
    () => [
      // {
      //   header: "ID",
      //   accessorKey: "id",
      //   enableColumnFilter: false,
      //   enableSorting: true,
      //   cell: ({ row }) => `#${row.original.id}`,
      // },
      {
        header: "Applicant",
        accessorKey: "applicantname",
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ row }) => (
          <div>
            <div className="fw-semibold">{row.original.applicantname || "N/A"}</div>
            <small className="text-muted">{row.original.applicantemail || "N/A"}</small>
          </div>
        ),
      },
      {
        header: "Contact",
        accessorKey: "contactNumber",
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ row }) => row.original.contactNumber || "N/A",
      },
      {
        header: "Applied For",
        accessorKey: "title",
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ row }) => (
          <div>
            <div>{row.original.title || "General Application"}</div>
            <small className="text-muted">
              Exp: {row.original.applicantexperience || "0"} yrs
            </small>
          </div>
        ),
      },
      {
        header: "Salary",
        accessorKey: "salary",
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ row }) => (
          <div>
            <div>Exp: {row.original.expectedSalary || "N/A"}</div>
            <small className="text-muted">
              Current: {row.original.currentSalary || "N/A"}
            </small>
          </div>
        ),
      },
      {
        header: "CV",
        accessorKey: "cv",
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ row }) => (
          row.original.cv ? (
            <button
              className="btn btn-sm btn-outline-primary"
              onClick={() => handleCVDownload(row.original.cv)}
            >
              <i className="fas fa-download"></i>
            </button>
          ) : (
            <span className="text-muted">-</span>
          )
        ),
      },
      // {
      //   header: "Interview Date & Link",
      //   accessorKey: "date",
      //   enableColumnFilter: false,
      //   enableSorting: true,
      //   cell: ({ row }) => (
      //     <div>
      //       <div>Date: {formatDate(row.original.interviewDate)}</div>
      //       <small className="text-muted">
      //         Link: {row.original.interviewlink || "N/A"}
      //       </small>
      //     </div>
      //   ),
      // },

      // {
      //   header: "Status",
      //   accessorKey: "status",
      //   enableColumnFilter: false,
      //   enableSorting: true,
      //   cell: ({ row }) => (
      //     <div
      //       dangerouslySetInnerHTML={{
      //         __html: getStatusBadge(row.original),
      //       }}
      //     />
      //   ),
      // },
      {
        header: "Applied Date",
        accessorKey: "date",
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ row }) => formatDate(row.original.date),
      },

      {
        header: "Action",
        accessorKey: "action",
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ row }) => (
          <div className="d-flex align-items-center gap-1">
            <button
              onClick={() => handleEditApplication(row.original)}
              className="btn btn-primary btn-sm"
            >
              <i className="fas fa-edit"></i>
            </button>
            <button
              className="btn btn-danger btn-sm"
              onClick={() => showDeleteModal(row.original.id)}
            >
              <i className="fas fa-trash"></i>
              {/* {deletingApplication ? "Deleting..." : "Delete"} */}
            </button>
          </div>
        ),
      },
    ],
    [deletingApplication]
  );

  if (isLoading || jobsLoading) {
    return <DashboardLoader />;
  }

  return (
    <div className="page-content">
      <div className="container-fluid">
        <Breadcrumb title="Job Applications" breadcrumbItem="Applications" />

        {/* Filters Section */}
        <div className="card mb-3">
          <div className="card-body">
            <div className="row g-3">
              <div className="col-md-3">
                <label className="form-label">Job Title</label>
                <select
                  className="form-select"
                  value={jobTitleFilter}
                  onChange={e => setJobTitleFilter(e.target.value)}
                >
                  <option value="">All Job Titles</option>
                  {uniqueJobTitles.map(title => (
                    <option key={title} value={title}>{title}</option>
                  ))}
                </select>
              </div>

              <div className="col-md-3">
                <label className="form-label">Status</label>
                <select
                  className="form-select"
                  value={statusFilter}
                  onChange={e => setStatusFilter(e.target.value)}
                >
                  <option value="">All Status</option>
                  <option value="applied">Applied</option>
                  <option value="selected">Selected</option>
                  <option value="rejected">Rejected</option>
                  <option value="scheduled">Interview Scheduled</option>
                  <option value="completed">Interview Done</option>
                </select>
              </div>

              <div className="col-md-3">
                <label className="form-label">Start Date</label>
                <input
                  type="date"
                  className="form-control"
                  value={startDate}
                  onChange={e => setStartDate(e.target.value)}
                />
              </div>

              <div className="col-md-3">
                <label className="form-label">End Date</label>
                <input
                  type="date"
                  className="form-control"
                  value={endDate}
                  onChange={e => setEndDate(e.target.value)}
                />
              </div>

              {/* <div className="col-12">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <span className="text-muted">
                      Showing {filteredApplications.length} of {applications?.length || 0} applications
                    </span>
                  </div>
                  <div>
                    <button
                      className="btn btn-light me-2"
                      onClick={handleClearFilters}
                    >
                      <i className="fas fa-times me-1"></i> Clear Filters
                    </button>
                    <Link to="/create-job-application" className="btn btn-primary">
                      <i className="fas fa-plus me-2"></i>New Application
                    </Link>
                  </div>
                </div>
              </div> */}
            </div>
          </div>
        </div>

        <TableContainer
          columns={columns}
          data={filteredApplications}
          isGlobalFilter={true}
          isPagination={true}
          SearchPlaceholder="Search applications..."
          pagination="pagination"
          paginationWrapper="dataTables_paginate paging_simple_numbers"
          tableClass="table-bordered table-nowrap dt-responsive nowrap w-100 dataTable no-footer dtr-inline"
        />

        <DeleteModal
          show={deleteModal}
          onDeleteClick={handleDeleteApplication}
          loader={deletingApplication}
          onCloseClick={() => setDeleteModal(false)}
        />

        {selectedApplication && (
          <EditJobApplicationModal
            isOpen={editModalOpen}
            toggle={() => setEditModalOpen(false)}
            application={selectedApplication}
            onApplicationUpdate={handleApplicationUpdate}
          />
        )}
      </div>
    </div>
  );
};

export default AllJobApplications;